import React, { useEffect, useState, useMemo } from "react";
import {
  Mail,
  Phone,
  Search,
  MessageSquare,
  Clock,
  Sparkles,
  RefreshCw,
  Inbox,
  X,
} from "lucide-react";

const API_BASE = "https://api.vanyacollectionsrajahmundry.com";

const AdminContactPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Fetch all contact messages
  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/contact/all`);
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const filteredMessages = useMemo(() => {
    if (!searchTerm.trim()) return messages;
    const term = searchTerm.toLowerCase();
    return messages.filter(
      (m) =>
        m.name?.toLowerCase().includes(term) ||
        m.email?.toLowerCase().includes(term) ||
        m.phone?.toLowerCase().includes(term) ||
        m.subject?.toLowerCase().includes(term) ||
        m.message?.toLowerCase().includes(term)
    );
  }, [messages, searchTerm]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <div style={styles.pageContainer}>
      {/* HEADER */}
      <div style={styles.headerRow}>
        <div>
          <div style={styles.badgeLabel}>
            <Sparkles size={13} color="#D4AF37" />
            <span>CUSTOMER SUPPORT & INQUIRIES</span>
          </div>
          <h1 style={styles.title}>Contact Messages</h1>
          <p style={styles.subtitle}>
            Inquiries and correspondence received from visitors and shoppers through the store contact form.
          </p>
        </div>

        <button
          onClick={fetchMessages}
          disabled={loading}
          style={styles.refreshBtn}
        >
          <RefreshCw size={15} className={loading ? "spin-icon" : ""} />
          <span>{loading ? "Refreshing..." : "Refresh"}</span>
        </button>
      </div>

      {/* METRICS */}
      <div style={styles.metricsRow}>
        <div style={styles.metricCard}>
          <div style={{ ...styles.metricIcon, background: "rgba(20, 53, 43, 0.08)", color: "#14352b" }}>
            <Inbox size={22} />
          </div>
          <div>
            <div style={styles.metricLabel}>Total Inquiries</div>
            <div style={styles.metricValue}>{messages.length}</div>
          </div>
        </div>

        <div style={styles.metricCard}>
          <div style={{ ...styles.metricIcon, background: "rgba(212, 175, 55, 0.12)", color: "#b89326" }}>
            <MessageSquare size={22} />
          </div>
          <div>
            <div style={styles.metricLabel}>Filtered Results</div>
            <div style={styles.metricValue}>{filteredMessages.length}</div>
          </div>
        </div>
      </div>

      {/* TABLE CARD */}
      <div style={styles.card}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color="#94a3b8" style={{ position: "absolute", left: 12 }} />
            <input
              type="text"
              placeholder="Search by customer name, email, phone or message..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                style={styles.clearBtn}
              >
                ×
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div style={styles.loadingState}>
            <RefreshCw size={26} color="#D4AF37" className="spin-icon" />
            <p style={{ marginTop: 12, color: "#64748b" }}>Loading customer inquiries...</p>
          </div>
        ) : filteredMessages.length === 0 ? (
          <div style={styles.emptyState}>
            <Inbox size={40} color="#d4af37" />
            <h3 style={{ margin: "14px 0 4px", color: "#1e293b", fontSize: "16px" }}>
              {searchTerm ? "No messages matching your search" : "No contact messages found"}
            </h3>
            <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>
              {searchTerm ? "Try searching for a different keyword." : "Messages sent through your contact page will appear here."}
            </p>
          </div>
        ) : (
          <div style={styles.tableResponsive}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.th}>Contact Info</th>
                  <th style={styles.th}>Subject</th>
                  <th style={styles.th}>Message Content</th>
                  <th style={styles.th}>Received Date</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMessages.map((msg, index) => (
                  <tr
                    key={msg.id || index}
                    style={{
                      ...styles.tr,
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafbfb",
                    }}
                  >
                    <td style={styles.td}>
                      <span style={styles.idNumber}>{index + 1}</span>
                    </td>

                    <td style={styles.td}>
                      <div style={styles.customerCell}>
                        <div style={styles.avatar}>
                          {getInitials(msg.name)}
                        </div>
                        <div>
                          <div style={styles.customerName}>{msg.name}</div>
                        </div>
                      </div>
                    </td>

                    <td style={styles.td}>
                      <div style={styles.contactDetails}>
                        <a href={`mailto:${msg.email}`} style={styles.contactLink}>
                          <Mail size={13} style={{ flexShrink: 0 }} />
                          <span>{msg.email}</span>
                        </a>
                        {msg.phone && (
                          <a href={`tel:${msg.phone}`} style={styles.contactLink}>
                            <Phone size={13} style={{ flexShrink: 0 }} />
                            <span>{msg.phone}</span>
                          </a>
                        )}
                      </div>
                    </td>

                    <td style={styles.td}>
                      <span style={styles.subjectBadge}>
                        {msg.subject || "General Inquiry"}
                      </span>
                    </td>

                    <td style={styles.td}>
                      <div style={styles.messageSnippet} title={msg.message}>
                        {msg.message}
                      </div>
                    </td>

                    <td style={styles.td}>
                      <div style={styles.dateCell}>
                        <Clock size={13} color="#94a3b8" />
                        <span>{formatDate(msg.created_at)}</span>
                      </div>
                    </td>

                    <td style={{ ...styles.td, textAlign: "right" }}>
                      <button
                        onClick={() => setSelectedMessage(msg)}
                        style={styles.viewBtn}
                      >
                        View Full
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MESSAGE MODAL */}
      {selectedMessage && (
        <div style={styles.modalOverlay} onClick={() => setSelectedMessage(null)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Inquiry from {selectedMessage.name}</h3>
                <span style={styles.modalDate}>{formatDate(selectedMessage.created_at)}</span>
              </div>
              <button
                onClick={() => setSelectedMessage(null)}
                style={styles.modalCloseBtn}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.modalInfoRow}>
                <div>
                  <div style={styles.modalLabel}>Email</div>
                  <a href={`mailto:${selectedMessage.email}`} style={styles.modalLink}>
                    <Mail size={14} />
                    {selectedMessage.email}
                  </a>
                </div>
                {selectedMessage.phone && (
                  <div>
                    <div style={styles.modalLabel}>Phone</div>
                    <a href={`tel:${selectedMessage.phone}`} style={styles.modalLink}>
                      <Phone size={14} />
                      {selectedMessage.phone}
                    </a>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16 }}>
                <div style={styles.modalLabel}>Subject</div>
                <div style={styles.modalSubject}>{selectedMessage.subject || "No Subject"}</div>
              </div>

              <div style={{ marginTop: 16 }}>
                <div style={styles.modalLabel}>Message</div>
                <div style={styles.modalMessageBox}>
                  {selectedMessage.message}
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <a
                href={`mailto:${selectedMessage.email}?subject=Re: ${encodeURIComponent(
                  selectedMessage.subject || "Vanya Collections Inquiry"
                )}`}
                style={styles.replyBtn}
              >
                <Mail size={16} />
                <span>Reply via Email</span>
              </a>
              <button
                onClick={() => setSelectedMessage(null)}
                style={styles.closeActionBtn}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin-icon {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

const styles = {
  pageContainer: {
    maxWidth: "1200px",
    margin: "0 auto",
    fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  badgeLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "1.2px",
    color: "#D4AF37",
    backgroundColor: "#14352b",
    padding: "4px 10px",
    borderRadius: "20px",
    marginBottom: "8px",
  },
  title: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#14352b",
    margin: "0 0 6px 0",
  },
  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    margin: 0,
    maxWidth: "650px",
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 16px",
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    border: "1.5px solid #e2e8f0",
    color: "#14352b",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },
  metricsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  metricCard: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "16px 20px",
    border: "1px solid #edf2f7",
    boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },
  metricIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  metricLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
    marginBottom: "4px",
    textTransform: "uppercase",
  },
  metricValue: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#0f172a",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
  },
  toolbar: {
    marginBottom: "18px",
  },
  searchBox: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    maxWidth: "460px",
  },
  searchInput: {
    width: "100%",
    padding: "10px 30px 10px 36px",
    borderRadius: "10px",
    border: "1.5px solid #cbd5e1",
    fontSize: "13.5px",
    outline: "none",
  },
  clearBtn: {
    position: "absolute",
    right: "10px",
    background: "transparent",
    border: "none",
    fontSize: "16px",
    color: "#94a3b8",
    cursor: "pointer",
  },
  loadingState: {
    padding: "50px",
    textAlign: "center",
  },
  emptyState: {
    padding: "50px",
    textAlign: "center",
  },
  tableResponsive: {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    borderRadius: "12px",
    border: "1px solid #edf2f7",
    width: "100%",
  },
  table: {
    width: "100%",
    minWidth: "750px",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "13.5px",
  },
  th: {
    padding: "14px 16px",
    backgroundColor: "#14352b",
    color: "#f5d470",
    fontWeight: "700",
    fontSize: "12px",
    textAlign: "left",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  tr: {
    transition: "background-color 0.15s ease",
  },
  td: {
    padding: "14px 16px",
    borderBottom: "1px solid #edf2f7",
    verticalAlign: "middle",
    color: "#334155",
  },
  idNumber: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#94a3b8",
  },
  customerCell: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  avatar: {
    width: "34px",
    height: "34px",
    borderRadius: "50%",
    backgroundColor: "#ecfdf5",
    color: "#065f46",
    fontWeight: "800",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #a7f3d0",
  },
  customerName: {
    fontWeight: "700",
    color: "#0f172a",
  },
  contactDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
  },
  contactLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    color: "#14352b",
    textDecoration: "none",
    fontSize: "12.5px",
  },
  subjectBadge: {
    display: "inline-block",
    padding: "3px 8px",
    borderRadius: "6px",
    backgroundColor: "#f1f5f9",
    color: "#334155",
    fontWeight: "600",
    fontSize: "12px",
  },
  messageSnippet: {
    maxWidth: "280px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#475569",
  },
  dateCell: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#64748b",
    fontSize: "12.5px",
    whiteSpace: "nowrap",
  },
  viewBtn: {
    padding: "6px 12px",
    borderRadius: "6px",
    backgroundColor: "#f8fafc",
    border: "1px solid #cbd5e1",
    color: "#14352b",
    fontWeight: "700",
    fontSize: "12px",
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(3px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modalCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    maxWidth: "540px",
    width: "100%",
    padding: "24px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: "16px",
    borderBottom: "1px solid #f1f5f9",
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#14352b",
    margin: "0 0 4px 0",
  },
  modalDate: {
    fontSize: "12px",
    color: "#64748b",
  },
  modalCloseBtn: {
    background: "transparent",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
  },
  modalBody: {
    padding: "20px 0",
  },
  modalInfoRow: {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
  },
  modalLabel: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#64748b",
    textTransform: "uppercase",
    marginBottom: "4px",
  },
  modalLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: "#14352b",
    fontWeight: "600",
    textDecoration: "none",
    fontSize: "13.5px",
  },
  modalSubject: {
    fontWeight: "700",
    color: "#1e293b",
    fontSize: "14px",
  },
  modalMessageBox: {
    backgroundColor: "#f8fafc",
    padding: "14px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    color: "#334155",
    fontSize: "14px",
    lineHeight: "1.6",
    whiteSpace: "pre-wrap",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    paddingTop: "16px",
    borderTop: "1px solid #f1f5f9",
  },
  replyBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "10px 18px",
    borderRadius: "8px",
    backgroundColor: "#14352b",
    color: "#d4af37",
    textDecoration: "none",
    fontWeight: "700",
    fontSize: "13px",
  },
  closeActionBtn: {
    padding: "10px 16px",
    borderRadius: "8px",
    backgroundColor: "#f1f5f9",
    border: "none",
    color: "#475569",
    fontWeight: "600",
    fontSize: "13px",
    cursor: "pointer",
  },
};

export default AdminContactPage;